/** Estado unificado de filtros (chat, busca avançada, URL). */

export const TAG_NO_TAG = "notag";

/** Abas em /search-results */
export const SEARCH_RESULTS_TAB_VALUES = ["atendimento", "compras", "clientes"];
export const SEARCH_RESULTS_TAB_DEFAULT = "atendimento";

export function normalizeSearchResultsTab(value) {
  const raw = String(value || "").trim().toLowerCase();
  return SEARCH_RESULTS_TAB_VALUES.includes(raw) ? raw : SEARCH_RESULTS_TAB_DEFAULT;
}

export function searchResultsTabToIndex(tab) {
  const idx = SEARCH_RESULTS_TAB_VALUES.indexOf(normalizeSearchResultsTab(tab));
  return idx >= 0 ? idx : 0;
}

export function searchResultsIndexToTab(index) {
  return SEARCH_RESULTS_TAB_VALUES[Number(index)] || SEARCH_RESULTS_TAB_DEFAULT;
}

/** Rótulos de status no filtro rápido / busca avançada */
export const QUICK_STATUS_FILTER_LABELS = [
  "Pendente",
  "Em Atendimento",
  "Contato Futuro",
  "Concluída",
  "Perdida",
  "Cancelada",
];

/** Status em aberto — habilita opção "Todo período" no dashboard. */
export const OPEN_STATUS_FILTER_LABELS = ["Pendente", "Em Atendimento", "Contato Futuro"];

export function hasOpenStatusFilters(filters) {
  const list = Array.isArray(filters) ? filters : [];
  return list.some((f) => OPEN_STATUS_FILTER_LABELS.includes(f));
}

/** Todo período: precisa ter status, e só status em aberto (sem concluída/perdida/cancelada). */
export function canEnableAllPeriod(filters) {
  const statuses = (Array.isArray(filters) ? filters : []).filter((f) =>
    QUICK_STATUS_FILTER_LABELS.includes(f)
  );
  if (!statuses.length) return false;
  return statuses.every((f) => OPEN_STATUS_FILTER_LABELS.includes(f));
}

/** Tamanho de página alinhado ao limit da API de conversas na busca. */
export const SEARCH_CONVERSATIONS_PAGE_SIZE = 50;

/** Contagem estilo Gmail: "50+" enquanto houver mais páginas, total exato no fim. */
export function formatSearchResultCount(loadedCount, hasNextPage) {
  const count = Number(loadedCount) || 0;
  if (count === 0) return "0";
  if (hasNextPage) return `${count}+`;
  return String(count);
}

/**
 * Contagem na aba de resultados: só mostra número após a query ter carregado.
 * Antes disso retorna null (a UI usa um sinal discreto, ex. …).
 */
export function formatSearchResultsTabCount({ loaded, count, hasNextPage = false }) {
  if (!loaded) return null;
  return formatSearchResultCount(count, hasNextPage);
}

/** Rótulos de responsabilidade no filtro rápido / busca avançada */
export const QUICK_OWNERSHIP_FILTER_LABELS = [
  "Minhas Solicitações",
  "Outros Usuários",
  "Fila",
  "ChatBots",
  "Agentes de IA",
];

/** Filtros rápidos que não são controlados pela seção Status do modal avançado */
export const QUICK_PRESERVED_FILTER_LABELS = [
  "Não Lidas",
  "Pipeline de Vendas",
  "WhatsApp pessoal",
];

const isQuickStatusFilter = (f) => QUICK_STATUS_FILTER_LABELS.includes(f);
const isQuickOwnershipFilter = (f) => QUICK_OWNERSHIP_FILTER_LABELS.includes(f);

/**
 * Mescla filtros rápidos após aplicar busca avançada: substitui status/responsável
 * vindos do modal e preserva demais pills (Não Lidas, Pipeline, etc.).
 */
export function mergeQuickFiltersAfterAdvancedApply(currentFilters, stateFilters) {
  const current = Array.isArray(currentFilters) ? currentFilters : [];
  const fromState = Array.isArray(stateFilters) ? stateFilters : [];

  const preserved = current.filter(
    (f) => !isQuickStatusFilter(f) && !isQuickOwnershipFilter(f)
  );
  const ownership = fromState.filter(isQuickOwnershipFilter);
  const status = fromState.filter(isQuickStatusFilter);

  return [...new Set([...preserved, ...ownership, ...status])];
}

export const EMPTY_SEARCH_FILTER_STATE = {
  q: "",
  filters: [],
  ids_tipos: [],
  ids_subtipo1: [],
  ids_subtipo2: [],
  ids_subtipo3: [],
  ids_origens: [],
  ids_usuarios: [],
  ids_usuarios_interacao: [],
  ids_agentes: [],
  ids_agentes_interacao: [],
  tags: [],
  data_inicio: "",
  data_fim: "",
  date_field: "entrada",
  tipo_responsavel: "",
  include_message_text: false,
  all_period: false,
  ids_produtos: [],
  ids_campaigns: [],
  ids_whatsapp_campaigns: [],
  ids_motivos: [],
  ids_potenciais: [],
  ops_preset: "",
  stale_days: 7,
  tab: SEARCH_RESULTS_TAB_DEFAULT,
};

const csv = (s) =>
  s
    ? String(s)
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean)
    : [];

const csvNums = (s) =>
  csv(s)
    .map((x) => Number(x))
    .filter((n) => Number.isFinite(n));

const csvStrings = (s) => csv(s).map((x) => String(x).trim()).filter(Boolean);

const normalizeIdStrings = (raw) => {
  if (Array.isArray(raw)) {
    return raw.map((x) => String(x).trim()).filter(Boolean);
  }
  return csvStrings(raw);
};

export function normalizeSearchFilterState(raw = {}) {
  const s = raw && typeof raw === "object" ? raw : {};
  const includeMsg =
    s.include_message_text === true ||
    String(s.include_message_text || "").trim().toLowerCase() === "1" ||
    String(s.include_message_text || "").trim().toLowerCase() === "true" ||
    String(s.include_message_text || "").trim().toLowerCase() === "sim";
  const allPeriodRaw =
    s.all_period === true ||
    String(s.all_period || "").trim().toLowerCase() === "1" ||
    String(s.all_period || "").trim().toLowerCase() === "true" ||
    String(s.all_period || "").trim().toLowerCase() === "sim";
  const filters = Array.isArray(s.filters) ? [...s.filters] : csv(s.filters);
  // Todo período só vale com status exclusivamente em aberto.
  const allPeriod = allPeriodRaw && canEnableAllPeriod(filters);

  return {
    q: typeof s.q === "string" ? s.q : "",
    filters,
    ids_tipos: Array.isArray(s.ids_tipos) ? s.ids_tipos.map(Number).filter(Number.isFinite) : csvNums(s.ids_tipos),
    ids_subtipo1: Array.isArray(s.ids_subtipo1)
      ? s.ids_subtipo1.map(Number).filter(Number.isFinite)
      : csvNums(s.ids_subtipo1),
    ids_subtipo2: Array.isArray(s.ids_subtipo2)
      ? s.ids_subtipo2.map(Number).filter(Number.isFinite)
      : csvNums(s.ids_subtipo2),
    ids_subtipo3: Array.isArray(s.ids_subtipo3)
      ? s.ids_subtipo3.map(Number).filter(Number.isFinite)
      : csvNums(s.ids_subtipo3),
    ids_origens: Array.isArray(s.ids_origens)
      ? s.ids_origens.map(Number).filter(Number.isFinite)
      : csvNums(s.ids_origens),
    ids_usuarios: Array.isArray(s.ids_usuarios)
      ? s.ids_usuarios.map(Number).filter(Number.isFinite)
      : csvNums(s.ids_usuarios),
    ids_usuarios_interacao: Array.isArray(s.ids_usuarios_interacao)
      ? s.ids_usuarios_interacao.map(Number).filter(Number.isFinite)
      : csvNums(s.ids_usuarios_interacao),
    ids_agentes: normalizeIdStrings(s.ids_agentes),
    ids_agentes_interacao: normalizeIdStrings(s.ids_agentes_interacao),
    tags: Array.isArray(s.tags) ? [...s.tags] : csv(s.tags),
    data_inicio: allPeriod ? "" : s.data_inicio || "",
    data_fim: allPeriod ? "" : s.data_fim || "",
    date_field: s.date_field === "atualizacao" ? "atualizacao" : "entrada",
    tipo_responsavel: ["fila", "humanos", "chatbots", "agentes"].includes(s.tipo_responsavel)
      ? s.tipo_responsavel
      : "",
    include_message_text: includeMsg,
    all_period: allPeriod,
    ids_produtos: Array.isArray(s.ids_produtos)
      ? s.ids_produtos.map(Number).filter((n) => Number.isFinite(n) && n > 0)
      : csvNums(s.ids_produtos).filter((n) => n > 0),
    ids_campaigns: Array.isArray(s.ids_campaigns)
      ? s.ids_campaigns.map(Number).filter((n) => Number.isFinite(n) && n > 0)
      : csvNums(s.ids_campaigns).filter((n) => n > 0),
    ids_whatsapp_campaigns: Array.isArray(s.ids_whatsapp_campaigns)
      ? s.ids_whatsapp_campaigns.map(Number).filter((n) => Number.isFinite(n) && n > 0)
      : csvNums(s.ids_whatsapp_campaigns).filter((n) => n > 0),
    ids_motivos: Array.isArray(s.ids_motivos)
      ? s.ids_motivos.map(Number).filter((n) => Number.isFinite(n) && n > 0)
      : csvNums(s.ids_motivos).filter((n) => n > 0),
    ids_potenciais: Array.isArray(s.ids_potenciais)
      ? s.ids_potenciais.map(Number).filter((n) => Number.isInteger(n) && n > 0)
      : csvNums(s.ids_potenciais).filter((n) => n > 0),
    ops_preset: ["overdue", "due_today", "stale"].includes(String(s.ops_preset || "").trim())
      ? String(s.ops_preset).trim()
      : "",
    stale_days: (() => {
      const n = Number(s.stale_days);
      if (!Number.isFinite(n) || n <= 0) return 7;
      return Math.min(Math.floor(n), 365);
    })(),
    tab: normalizeSearchResultsTab(s.tab),
  };
}

export function parseSearchParamsFromUrl(search) {
  const params = new URLSearchParams(search);
  const includeRaw = (params.get("include_message_text") || "").trim().toLowerCase();
  return normalizeSearchFilterState({
    q: params.get("q") || "",
    filters: csv(params.get("filters")),
    data_inicio: params.get("data_inicio") || "",
    data_fim: params.get("data_fim") || "",
    date_field: params.get("date_field") || "entrada",
    tipo_responsavel: params.get("tipo_responsavel") || "",
    ids_tipos: csv(params.get("ids_tipos")),
    ids_subtipo1: csv(params.get("ids_subtipo1")),
    ids_subtipo2: csv(params.get("ids_subtipo2")),
    ids_subtipo3: csv(params.get("ids_subtipo3")),
    ids_origens: csv(params.get("ids_origens")),
    ids_usuarios: csv(params.get("ids_usuarios")),
    ids_usuarios_interacao: csv(params.get("ids_usuarios_interacao")),
    ids_agentes: csv(params.get("ids_agentes")),
    ids_agentes_interacao: csv(params.get("ids_agentes_interacao")),
    tags: csv(params.get("tags")),
    include_message_text: includeRaw,
    tab: params.get("tab") || SEARCH_RESULTS_TAB_DEFAULT,
  });
}

export function filterStateToUrlParams(state) {
  const s = normalizeSearchFilterState(state);
  const params = new URLSearchParams();
  if (s.q.trim()) params.set("q", s.q.trim());
  if (s.filters.length) params.set("filters", s.filters.join(","));
  if (s.data_inicio) params.set("data_inicio", s.data_inicio);
  if (s.data_fim) params.set("data_fim", s.data_fim);
  if (s.date_field && s.date_field !== "entrada") params.set("date_field", s.date_field);
  if (s.tipo_responsavel) params.set("tipo_responsavel", s.tipo_responsavel);
  if (s.ids_tipos.length) params.set("ids_tipos", s.ids_tipos.join(","));
  if (s.ids_subtipo1.length) params.set("ids_subtipo1", s.ids_subtipo1.join(","));
  if (s.ids_subtipo2.length) params.set("ids_subtipo2", s.ids_subtipo2.join(","));
  if (s.ids_subtipo3.length) params.set("ids_subtipo3", s.ids_subtipo3.join(","));
  if (s.ids_origens.length) params.set("ids_origens", s.ids_origens.join(","));
  if (s.ids_usuarios.length) params.set("ids_usuarios", s.ids_usuarios.join(","));
  if (s.ids_usuarios_interacao.length)
    params.set("ids_usuarios_interacao", s.ids_usuarios_interacao.join(","));
  if (s.ids_agentes.length) params.set("ids_agentes", s.ids_agentes.join(","));
  if (s.ids_agentes_interacao.length)
    params.set("ids_agentes_interacao", s.ids_agentes_interacao.join(","));
  if (s.tags.length) params.set("tags", s.tags.join(","));
  if (s.include_message_text) params.set("include_message_text", "1");
  if (s.tab && s.tab !== SEARCH_RESULTS_TAB_DEFAULT) params.set("tab", s.tab);
  return params;
}

export function filterStateToUrlString(state) {
  return filterStateToUrlParams(state).toString();
}

export function isProtocolSearchQuery(q) {
  const term = String(q || "").trim();
  if (!term) return false;
  const idPart = term.startsWith("#") ? term.slice(1).trim() : term;
  return /^\d+$/.test(idPart);
}

/** Normaliza termo de protocolo (#123 → 123); demais termos permanecem iguais. */
export function normalizeProtocolSearchQuery(q) {
  const term = String(q || "").trim();
  if (!term) return "";
  if (!isProtocolSearchQuery(term)) return term;
  return term.startsWith("#") ? term.slice(1).trim() : term;
}

/** Busca textual (não protocolo) com 3+ caracteres deve incluir texto das mensagens no servidor. */
export function shouldAutoIncludeMessageTextSearch(q) {
  const term = String(q || "").trim();
  return term.length >= 3 && !isProtocolSearchQuery(term);
}

/** Estado atual da lista do chat → estado para URL de /search-results */
export function chatListStateToSearchFilterState(searchTerm, filters, advancedFilterState) {
  const normalized = normalizeSearchFilterState(advancedFilterState || EMPTY_SEARCH_FILTER_STATE);
  return normalizeSearchFilterState({
    ...normalized,
    q: typeof searchTerm === "string" ? searchTerm : "",
    filters: Array.isArray(filters) ? filters : [],
  });
}

/**
 * Lista do chat sem resultado → /search-results com busca ampliada.
 * Com termo de 3+ caracteres, habilita busca no texto das mensagens no servidor.
 * Protocolo numérico limpa filtros Meu dia (lookup direto do ID).
 */
export function chatListStateToExpandedSearchFilterState(searchTerm, filters, advancedFilterState) {
  const state = chatListStateToSearchFilterState(searchTerm, filters, advancedFilterState);
  const q = (state.q || "").trim();
  if (!q) return state;

  const base = {
    ...state,
    data_inicio: "",
    data_fim: "",
    date_field: "entrada",
  };

  if (isProtocolSearchQuery(q)) {
    return normalizeSearchFilterState({
      ...base,
      q: normalizeProtocolSearchQuery(q),
      filters: [],
      include_message_text: false,
    });
  }

  if (shouldAutoIncludeMessageTextSearch(q)) {
    return normalizeSearchFilterState({
      ...base,
      include_message_text: true,
    });
  }

  return normalizeSearchFilterState(base);
}

/** Payload do AdvancedSearchModal → estado normalizado */
export function modalPayloadToFilterState(payload = {}) {
  const filtersOut = Array.isArray(payload.filters) ? payload.filters : [];
  const hasExplicitStatusFilters = Array.isArray(payload.statusFilters);
  const statusFromModal = hasExplicitStatusFilters ? payload.statusFilters : null;

  let mergedFilters;
  if (hasExplicitStatusFilters) {
    const nonStatus = filtersOut.filter((f) => !isQuickStatusFilter(f));
    mergedFilters = [...new Set([...nonStatus, ...statusFromModal])];
  } else {
    mergedFilters = filtersOut;
  }

  return normalizeSearchFilterState({
    q: payload.q || "",
    filters: mergedFilters,
    ids_tipos: payload.ids_tipos,
    ids_subtipo1: payload.ids_subtipo1,
    ids_subtipo2: payload.ids_subtipo2,
    ids_subtipo3: payload.ids_subtipo3,
    ids_origens: payload.ids_origens,
    ids_usuarios: payload.ids_usuarios,
    ids_usuarios_interacao: payload.ids_usuarios_interacao,
    ids_agentes: payload.ids_agentes,
    ids_agentes_interacao: payload.ids_agentes_interacao,
    tags: payload.tags,
    data_inicio: payload.all_period ? "" : payload.data_inicio,
    data_fim: payload.all_period ? "" : payload.data_fim,
    date_field: payload.date_field,
    tipo_responsavel: payload.tipo_responsavel,
    include_message_text: payload.include_message_text,
    all_period: payload.all_period,
    ids_produtos: payload.ids_produtos,
    ids_campaigns: payload.ids_campaigns,
    ids_whatsapp_campaigns: payload.ids_whatsapp_campaigns,
    ids_motivos: payload.ids_motivos,
    ids_potenciais: payload.ids_potenciais,
    ops_preset: payload.ops_preset,
    stale_days: payload.stale_days,
    tab: payload.tab,
  });
}

/** Estado → params avançados para useConversations (sem q/filters quick) */
export function filterStateToAdvancedParams(state, extra = {}) {
  const s = normalizeSearchFilterState(state);
  const hasDates = s.data_inicio || s.data_fim;
  const hasIds =
    s.ids_tipos.length +
      s.ids_subtipo1.length +
      s.ids_subtipo2.length +
      s.ids_subtipo3.length +
      s.ids_origens.length +
      s.ids_usuarios.length +
      s.ids_usuarios_interacao.length +
      s.ids_agentes.length +
      s.ids_agentes_interacao.length >
    0;
  const hasTags = s.tags.length > 0;
  const hasTipoResp = Boolean(s.tipo_responsavel);
  const hasTrackC =
    s.ids_produtos.length > 0 ||
    s.ids_campaigns.length > 0 ||
    s.ids_whatsapp_campaigns.length > 0 ||
    s.ids_motivos.length > 0 ||
    s.ids_potenciais.length > 0 ||
    Boolean(s.ops_preset);

  if (!hasDates && !hasIds && !hasTags && !hasTipoResp && !s.include_message_text && !hasTrackC) {
    return extra && Object.keys(extra).length ? { ...extra } : null;
  }

  return {
    ...(extra || {}),
    ...(hasDates ? { data_inicio: s.data_inicio, data_fim: s.data_fim, date_field: s.date_field } : {}),
    ...(s.ids_tipos.length ? { ids_tipos: s.ids_tipos } : {}),
    ...(s.ids_subtipo1.length ? { ids_subtipo1: s.ids_subtipo1 } : {}),
    ...(s.ids_subtipo2.length ? { ids_subtipo2: s.ids_subtipo2 } : {}),
    ...(s.ids_subtipo3.length ? { ids_subtipo3: s.ids_subtipo3 } : {}),
    ...(s.ids_origens.length ? { ids_origens: s.ids_origens } : {}),
    ...(s.ids_usuarios.length ? { ids_usuarios: s.ids_usuarios } : {}),
    ...(s.ids_usuarios_interacao.length ? { ids_usuarios_interacao: s.ids_usuarios_interacao } : {}),
    ...(s.ids_agentes.length ? { ids_agentes: s.ids_agentes } : {}),
    ...(s.ids_agentes_interacao.length ? { ids_agentes_interacao: s.ids_agentes_interacao } : {}),
    ...(s.tags.length ? { tags: s.tags } : {}),
    ...(hasTipoResp ? { tipo_responsavel: s.tipo_responsavel } : {}),
    ...(s.include_message_text ? { include_message_text: true } : {}),
    ...(s.ids_produtos.length ? { ids_produtos: s.ids_produtos } : {}),
    ...(s.ids_campaigns.length ? { ids_campaigns: s.ids_campaigns } : {}),
    ...(s.ids_whatsapp_campaigns.length ? { ids_whatsapp_campaigns: s.ids_whatsapp_campaigns } : {}),
    ...(s.ids_motivos.length ? { ids_motivos: s.ids_motivos } : {}),
    ...(s.ids_potenciais.length ? { ids_potenciais: s.ids_potenciais } : {}),
    ...(s.ops_preset ? { ops_preset: s.ops_preset } : {}),
    ...(s.ops_preset === "stale" ? { stale_days: s.stale_days } : {}),
  };
}

export function filterStateHasAdvancedCriteria(state) {
  const adv = filterStateToAdvancedParams(state);
  return adv != null && Object.keys(adv).some((k) => !["tipo_scope", "default_date_window_days"].includes(k));
}

export function filterStateToModalInitial(state) {
  const s = normalizeSearchFilterState(state);
  const statusFilters = s.filters.filter(isQuickStatusFilter);
  return {
    statusFilters,
    filters: s.filters,
    data_inicio: s.data_inicio,
    data_fim: s.data_fim,
    date_field: s.date_field,
    tipo_responsavel: s.tipo_responsavel,
    ids_tipos: s.ids_tipos,
    ids_subtipo1: s.ids_subtipo1,
    ids_subtipo2: s.ids_subtipo2,
    ids_subtipo3: s.ids_subtipo3,
    ids_origens: s.ids_origens,
    ids_usuarios: s.ids_usuarios,
    ids_usuarios_interacao: s.ids_usuarios_interacao,
    ids_agentes: s.ids_agentes,
    ids_agentes_interacao: s.ids_agentes_interacao,
    tags: s.tags,
    include_message_text: s.include_message_text,
    all_period: s.all_period,
    ids_produtos: s.ids_produtos,
    ids_campaigns: s.ids_campaigns,
    ids_whatsapp_campaigns: s.ids_whatsapp_campaigns,
    ids_motivos: s.ids_motivos,
    ids_potenciais: s.ids_potenciais,
    ops_preset: s.ops_preset,
    stale_days: s.stale_days,
    tab: s.tab,
  };
}

/** Mapa de labels de status do modal → ids numéricos (leads.status). */
export const STATUS_FILTER_LABEL_TO_ID = {
  Pendente: 0,
  "Em Atendimento": 1,
  Concluída: 2,
  "Contato Futuro": 3,
  Perdida: 4,
  Cancelada: 5,
};

export function statusFiltersToIds(labels) {
  const list = Array.isArray(labels) ? labels : [];
  const ids = [];
  list.forEach((label) => {
    const id = STATUS_FILTER_LABEL_TO_ID[label];
    if (id !== undefined) ids.push(id);
  });
  return [...new Set(ids)];
}

/**
 * Estado → params do dashboard atendimento (só selects/multiselects + período).
 * Sem q / search / include_message_text.
 */
export function filterStateToDashboardParams(state) {
  const s = normalizeSearchFilterState(state);
  const dateField = s.date_field === "atualizacao" ? "atualizacao" : "entrada";
  const statusIds = statusFiltersToIds(s.filters.filter(isQuickStatusFilter));
  const params = {};

  if (s.all_period) {
    params.all_period = 1;
  } else {
    if (s.data_inicio) params.start = s.data_inicio;
    if (s.data_fim) params.end = s.data_fim;
  }
  if (dateField === "atualizacao") params.date_field = "atualizacao";

  if (statusIds.length) params.ids_status = statusIds;
  if (s.ids_tipos.length) params.ids_tipos = s.ids_tipos;
  if (s.ids_subtipo1.length) params.ids_subtipo1 = s.ids_subtipo1;
  if (s.ids_subtipo2.length) params.ids_subtipo2 = s.ids_subtipo2;
  if (s.ids_subtipo3.length) params.ids_subtipo3 = s.ids_subtipo3;
  if (s.ids_origens.length) params.ids_origens = s.ids_origens;
  if (s.ids_usuarios.length) params.ids_usuarios = s.ids_usuarios;
  if (s.ids_agentes.length) params.ids_agentes = s.ids_agentes;
  if (s.ids_usuarios_interacao.length) params.ids_usuarios_interacao = s.ids_usuarios_interacao;
  if (s.ids_agentes_interacao.length) params.ids_agentes_interacao = s.ids_agentes_interacao;
  if (s.tags.length) params.tags = s.tags;
  if (s.tipo_responsavel) params.tipo_responsavel = s.tipo_responsavel;
  if (s.ids_produtos.length) params.ids_produtos = s.ids_produtos;
  if (s.ids_campaigns.length) params.ids_campaigns = s.ids_campaigns;
  if (s.ids_whatsapp_campaigns.length) params.ids_whatsapp_campaigns = s.ids_whatsapp_campaigns;
  if (s.ids_motivos.length) params.ids_motivos = s.ids_motivos;
  if (s.ids_potenciais.length) params.ids_potenciais = s.ids_potenciais;
  if (s.ops_preset) params.ops_preset = s.ops_preset;
  if (s.ops_preset === "stale") params.stale_days = s.stale_days;

  return params;
}

/** Params avançados para Live/conversations (sem texto / include_message_text). */
export function filterStateToLiveAdvancedParams(state, extra = {}) {
  const s = normalizeSearchFilterState(state);
  const adv = filterStateToAdvancedParams(
    s.all_period ? { ...s, data_inicio: "", data_fim: "" } : s,
    {
      ...extra,
      ...(s.all_period ? { default_date_window_days: 0 } : {}),
    }
  );
  if (!adv && !s.all_period) {
    return extra && Object.keys(extra).length ? { ...extra } : null;
  }
  const { include_message_text: _omit, ...rest } = adv || {};
  if (s.all_period) {
    delete rest.data_inicio;
    delete rest.data_fim;
    rest.default_date_window_days = 0;
  }
  return Object.keys(rest).length ? rest : extra && Object.keys(extra).length ? { ...extra } : null;
}

/** Conta critérios avançados (selects) ativos, ignorando só datas se desejado. */
export function countDashboardAdvancedCriteria(state, { includeDates = false } = {}) {
  const params = filterStateToDashboardParams(state);
  const keys = Object.keys(params).filter((k) => {
    if (!includeDates && (k === "start" || k === "end")) return false;
    return true;
  });
  return keys.length;
}

/** Fingerprint estável para scope key de jobs (ordem determinística). */
export function dashboardAdvancedFiltersFingerprint(state) {
  const params = filterStateToDashboardParams(state);
  const parts = Object.keys(params)
    .sort()
    .map((k) => {
      const v = params[k];
      if (Array.isArray(v)) return `${k}=${[...v].map(String).sort().join(",")}`;
      return `${k}=${String(v)}`;
    });
  return parts.join("&");
}
